import { cn } from '@/lib/utils';
import type { HighlightTemplate, SlideLayoutProps } from '../types';
import {
  BodyContent,
  LEADING,
  PADDING,
  SlideText,
  TYPE,
  fitTitle,
  formatPageLabel,
  hasNextSlide,
  shouldShowCounter,
} from '../_shared';

// "Distorted Lime" - vibrant lime surface with a straight darkened grid
// pattern, all-black display caps, and white squiggle decorations.
// Top bar carries the concept name + year (cover) or counter (body);
// bottom bar shows a SWIPE / NEXT cue when there is a next slide.

const LIME = '#C9EE45';
const INK = '#0A0A0A';

function GridPattern({ cellSize = 52 }: { cellSize?: number }) {
  const style: React.CSSProperties = {
    backgroundImage: `
      linear-gradient(to right, rgba(0,0,0,0.18) 1px, transparent 1px),
      linear-gradient(to bottom, rgba(0,0,0,0.18) 1px, transparent 1px)
    `,
    backgroundSize: `${cellSize}px ${cellSize}px`,
  };
  return (
    <div
      className='pointer-events-none absolute inset-0'
      style={style}
      aria-hidden='true'
    />
  );
}

function CornerLoop({ position }: { position: 'top-right' | 'bottom-left' }) {
  const placement =
    position === 'top-right'
      ? 'right-8 top-16 md:right-12 md:top-20'
      : 'bottom-24 left-8 md:bottom-32 md:left-12';
  return (
    <svg
      className={cn(
        'pointer-events-none absolute h-20 w-20 md:h-24 md:w-24',
        placement
      )}
      viewBox='0 0 100 100'
      fill='none'
      aria-hidden='true'
    >
      <path
        d='M 60 20 C 95 25, 95 65, 55 70 C 25 73, 15 50, 40 45 C 55 42, 65 55, 50 65'
        stroke='white'
        strokeWidth='3'
        strokeLinecap='round'
      />
    </svg>
  );
}

function ScrollArrow({ className }: { className?: string }) {
  return (
    <svg
      viewBox='0 0 110 80'
      fill='none'
      aria-hidden='true'
      className={cn('pointer-events-none', className)}
    >
      <path
        d='M 8 20 Q 35 8, 70 30 T 100 55'
        stroke='white'
        strokeWidth='3.5'
        strokeLinecap='round'
        fill='none'
      />
      <path
        d='M 80 50 L 100 55 L 92 38'
        stroke='white'
        strokeWidth='3.5'
        strokeLinecap='round'
        strokeLinejoin='round'
        fill='none'
      />
    </svg>
  );
}

function TopBar({ left, right }: { left?: string; right?: string }) {
  return (
    <div
      className={cn(
        'relative z-10 flex items-center justify-between',
        PADDING.slideX,
        'pt-10 md:pt-12'
      )}
    >
      <span
        className={cn('font-medium uppercase tracking-[0.12em]', TYPE.topBar)}
        style={{ color: INK }}
      >
        <SlideText text={left ?? ''} />
      </span>
      {right && (
        <span
          className={cn(
            'font-extrabold tabular-nums tracking-tight',
            TYPE.page
          )}
          style={{ color: INK }}
        >
          {right}
        </span>
      )}
    </div>
  );
}

function BottomBar({ right }: { right?: string }) {
  return (
    <div
      className={cn(
        'relative z-10 flex items-center justify-end',
        PADDING.slideX,
        'pb-10 md:pb-12'
      )}
    >
      {right && (
        <span
          className={cn(
            'font-extrabold uppercase tracking-[0.12em]',
            TYPE.topBar
          )}
          style={{ color: INK }}
        >
          {right}
        </span>
      )}
    </div>
  );
}

function CoverLayout({
  card,
  slideIndex,
  totalSlides,
  conceptName,
}: SlideLayoutProps) {
  const showSwipe = hasNextSlide(slideIndex, totalSlides);

  return (
    <div
      className='relative h-full w-full overflow-hidden'
      style={{ backgroundColor: LIME }}
    >
      <GridPattern />
      <CornerLoop position='top-right' />

      <div className='relative z-10 flex h-full w-full flex-col'>
        {/* Read at render, not at module load, and through Date.now() so a
            story's frozen clock (`freezeClock`) pins it for snapshots. */}
        <TopBar
          left={conceptName}
          right={String(new Date(Date.now()).getFullYear())}
        />

        <div className={cn('flex flex-1 items-center', PADDING.slideX)}>
          <h1
            className={cn(
              'text-balance font-extrabold uppercase tracking-tight',
              fitTitle(card.title),
              LEADING.display
            )}
            style={{ color: INK }}
          >
            <SlideText text={card.title} />
          </h1>
        </div>

        <BottomBar right={showSwipe ? 'SWIPE' : undefined} />
      </div>
    </div>
  );
}

function ContentLayout({
  card,
  slideIndex,
  totalSlides,
  conceptName,
}: SlideLayoutProps) {
  const showNext = hasNextSlide(slideIndex, totalSlides);
  const showCounter = shouldShowCounter(slideIndex, totalSlides);
  const arrowOnLeft = slideIndex % 2 === 1;

  return (
    <div
      className='relative h-full w-full overflow-hidden'
      style={{ backgroundColor: LIME }}
    >
      <GridPattern />
      <CornerLoop position={arrowOnLeft ? 'bottom-left' : 'top-right'} />

      <div className='relative z-10 flex h-full w-full flex-col'>
        <TopBar
          left={conceptName}
          right={
            showCounter ? formatPageLabel(slideIndex, totalSlides) : undefined
          }
        />

        <div
          className={cn(
            'flex flex-1 flex-col justify-center gap-8',
            PADDING.slideX
          )}
        >
          <div className='flex items-start gap-4 md:gap-6'>
            <ScrollArrow className='-mt-1 hidden h-12 w-16 shrink-0 -rotate-6 md:block' />
            <h2
              className={cn(
                'text-balance font-extrabold uppercase tracking-tight',
                fitTitle(card.title, 'body'),
                LEADING.display
              )}
              style={{ color: INK }}
            >
              <SlideText text={card.title} />
            </h2>
          </div>

          {card.body && (
            <div className='max-w-xl'>
              <BodyContent
                body={card.body}
                visual={card.visual}
                className='border-l-[3px] border-black pl-5 font-medium'
                style={{ color: INK }}
                spacing='space-y-2'
              />
            </div>
          )}
        </div>

        <BottomBar right={showNext ? 'NEXT' : undefined} />
      </div>
    </div>
  );
}

function ThumbnailLayout({ card, conceptName, totalSlides }: SlideLayoutProps) {
  return (
    <div
      className='relative h-full w-full overflow-hidden'
      style={{ backgroundColor: LIME }}
    >
      <GridPattern cellSize={20} />

      <div className='relative z-10 flex h-full flex-col p-5'>
        {conceptName && (
          <span
            className='line-clamp-1 text-xs font-medium uppercase tracking-[0.12em]'
            style={{ color: INK }}
          >
            <SlideText text={conceptName} />
          </span>
        )}

        <div className='flex flex-1 items-center'>
          <h3
            className={cn(
              'line-clamp-3 font-extrabold uppercase tracking-tight',
              fitTitle(card.title, 'thumb'),
              LEADING.display
            )}
            style={{ color: INK }}
          >
            <SlideText text={card.title} />
          </h3>
        </div>

        {totalSlides > 1 && (
          <div className='flex items-center justify-end'>
            <span
              className='text-xs font-extrabold uppercase tracking-[0.12em]'
              style={{ color: INK }}
            >
              SWIPE
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export const Template06: HighlightTemplate = {
  id: 'template-06',
  name: 'Distorted Lime',
  layouts: {
    cover: CoverLayout,
    body: ContentLayout,
    summary: ContentLayout,
  },
  thumbnail: ThumbnailLayout,
};
